import { Suspense, useCallback, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { VIEW_REGISTRY } from '@/lib/workspace/registry'
import { ViewInstanceContext, type ViewInstanceInfo } from '@/lib/workspace/viewInstance'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import type { PanelNode } from '@/lib/workspace/types'
import { PanelHeader } from './PanelHeader'

interface PanelContainerProps {
  panel: PanelNode
  compact?: boolean
}

function PanelLoader() {
  return (
    <div className="flex h-full items-center justify-center text-muted-foreground font-mono text-xs">
      Loading…
    </div>
  )
}

export function PanelContainer({ panel, compact }: PanelContainerProps) {
  const focusedPanelId = useWorkspaceStore((s) => s.focusedPanelId)
  const maximizedPanelId = useWorkspaceStore((s) => s.maximizedPanelId)
  const focusPanel = useWorkspaceStore((s) => s.focusPanel)

  const isFocused = focusedPanelId === panel.id
  const isMaximized = maximizedPanelId === panel.id

  const handleFocus = useCallback(() => {
    if (!isFocused) focusPanel(panel.id)
  }, [isFocused, panel.id, focusPanel])

  const active = panel.views.find((v) => v.instanceId === panel.activeViewInstanceId)
  const def = active ? VIEW_REGISTRY[active.viewId] : null
  const Component = def?.component

  // Identity for the mounted view: what `useViewState` scopes against, and how
  // a page reads the params it was opened with. Memoized so a panel re-render
  // (focus, resize) doesn't cascade into every consumer.
  const instanceInfo = useMemo<ViewInstanceInfo | null>(
    () => (active
      ? {
          instanceId: active.instanceId,
          panelId: panel.id,
          viewId: active.viewId,
          params: active.params,
        }
      : null),
    [active, panel.id],
  )

  const body = Component && active && instanceInfo ? (
    <ErrorBoundary key={active.instanceId}>
      <Suspense fallback={<PanelLoader />} key={active.instanceId}>
        <ViewInstanceContext.Provider value={instanceInfo}>
          <Component />
        </ViewInstanceContext.Provider>
      </Suspense>
    </ErrorBoundary>
  ) : null

  return (
    <div
      className={cn(
        'flex h-full flex-col overflow-hidden bg-background',
        isFocused && !compact && 'ring-1 ring-primary/15',
      )}
      onMouseDown={handleFocus}
    >
      <PanelHeader panel={panel} isFocused={isFocused} isMaximized={isMaximized} compact={compact} />
      {def?.fullBleed ? (
        <div className="flex-1 min-h-0 overflow-hidden">{body}</div>
      ) : (
        <div className="flex-1 overflow-y-auto scrollbar-thin">
          <div className="container py-2 md:py-6 max-w-none px-4 md:px-6">
            {body ?? (
              <div className="flex h-full items-center justify-center text-muted-foreground text-sm">
                No view selected
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
